import { resolveBaseUrl } from "@riq/api";
import { CONNECTION_EGRESS_LABELS } from "@riq/ui/copy";
import { faMotion } from "@riq/ui/design-tokens";
import { embedCopy } from "./embed-copy.js";
import { LinearPlaybackController, type LinearPlaybackSnapshot } from "./linear-controller.js";
import { EMBED_STYLES } from "./styles.js";
import { normalizeEmbedTheme } from "./themes.js";

const TAG_NAME = "riq-linear-player";

function resolveApiUrl(raw: string | null): string | null {
  if (raw != null && raw.trim().length > 0) {
    const base = resolveBaseUrl({ apiUrl: raw, inBrowser: true, isDev: false });
    return base.length > 0 ? base : null;
  }
  return defaultApiUrlFromScript();
}

function defaultApiUrlFromScript(): string | null {
  if (typeof document === "undefined") return null;
  const scripts = document.querySelectorAll("script[src]");
  for (const script of scripts) {
    const src = script.getAttribute("src");
    if (!src?.includes("riq-player")) continue;
    try {
      const url = new URL(src, document.baseURI);
      return `${url.protocol}//${url.host}`;
    } catch {}
  }
  return null;
}

function buttonLabel(snap: LinearPlaybackSnapshot): string {
  if (!snap.linearAvailable && !snap.isPlaying && !snap.isTuningIn) {
    return embedCopy.badge.offline;
  }
  if (snap.isPlaying) return embedCopy.live.stopListening;
  if (snap.isTuningIn) {
    if (snap.connectionStep) return CONNECTION_EGRESS_LABELS[snap.connectionStep];
    return embedCopy.live.connecting;
  }
  return embedCopy.live.tuneIn;
}

function buttonClass(snap: LinearPlaybackSnapshot): string {
  if (snap.isPlaying) return "btn btn--stop";
  if (!snap.linearAvailable) return "btn btn--offline";
  if (snap.isTuningIn) return "btn btn--primary btn--loading";
  return "btn btn--primary";
}

export class RiqLinearPlayerElement extends HTMLElement {
  static readonly observedAttributes = ["api-url", "title", "theme"];

  #shadow: ShadowRoot;
  #controller: LinearPlaybackController | null = null;
  #unsubscribe: (() => void) | null = null;
  #attachedApiUrl: string | null = null;
  /** Last rendered title, so the crossfade only fires on an actual track change. */
  #lastTitle: string | null = null;
  #changeTimer: ReturnType<typeof setTimeout> | null = null;

  #els: {
    badge: HTMLElement;
    title: HTMLElement;
    speaker: HTMLElement;
    button: HTMLButtonElement;
    error: HTMLElement;
    configError: HTMLElement;
    card: HTMLElement;
  };

  constructor() {
    super();
    this.#shadow = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = EMBED_STYLES;
    this.#shadow.appendChild(style);

    this.#els = {
      card: document.createElement("div"),
      badge: document.createElement("div"),
      title: document.createElement("h2"),
      speaker: document.createElement("p"),
      button: document.createElement("button"),
      error: document.createElement("p"),
      configError: document.createElement("p"),
    };

    this.#els.card.className = "card";
    this.#els.badge.className = "badge";
    this.#els.title.className = "track-title";
    this.#els.speaker.className = "track-speaker";
    this.#els.button.type = "button";
    this.#els.error.className = "error";
    this.#els.error.setAttribute("role", "alert");
    this.#els.configError.className = "config-error";

    this.#els.card.append(
      this.#els.badge,
      this.#els.title,
      this.#els.speaker,
      this.#els.button,
      this.#els.error,
      this.#els.configError,
    );
    this.#shadow.appendChild(this.#els.card);

    this.#els.button.addEventListener("click", () => {
      void this.#controller?.toggleLinear();
    });
  }

  connectedCallback(): void {
    this.#applyThemeAttribute();
    this.#attachFromAttributes();
  }

  disconnectedCallback(): void {
    this.#detachController();
    if (this.#changeTimer !== null) {
      clearTimeout(this.#changeTimer);
      this.#changeTimer = null;
    }
    this.#els.card.classList.remove("is-changing");
    this.#lastTitle = null;
  }

  attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
    if (!this.isConnected) return;
    if (name === "theme") {
      this.#applyThemeAttribute();
      return;
    }
    if (name === "title") {
      this.#applyDocumentTitle(value);
      return;
    }
    if (name === "api-url") {
      if (_old === value) return;
      if (_old === null) return;
      this.#attachFromAttributes();
    }
  }

  #detachController(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#controller?.dispose();
    this.#controller = null;
    this.#attachedApiUrl = null;
  }

  #attachFromAttributes(): void {
    const apiUrl = resolveApiUrl(this.getAttribute("api-url"));
    if (!apiUrl) {
      this.#detachController();
      this.#showConfigError(embedCopy.errors.missingApiUrl);
      return;
    }

    if (this.#controller && this.#attachedApiUrl === apiUrl) return;

    this.#els.configError.hidden = true;
    this.#detachController();
    this.#attachedApiUrl = apiUrl;
    this.#controller = new LinearPlaybackController(apiUrl);
    this.#unsubscribe = this.#controller.onChange((snap) => this.#render(snap));
    this.#controller.start();
    this.#render(this.#controller.snapshot);
    this.#applyDocumentTitle(this.getAttribute("title"));
  }

  #applyThemeAttribute(): void {
    const raw = this.getAttribute("theme");
    if (raw === null) return;
    const normalized = normalizeEmbedTheme(raw);
    if (normalized === "dark" && raw !== "dark") {
      this.removeAttribute("theme");
    }
  }

  #applyDocumentTitle(value: string | null): void {
    if (value?.trim()) {
      document.title = value.trim();
    }
  }

  #showConfigError(message: string): void {
    this.#els.configError.textContent = message;
    this.#els.configError.hidden = false;
    this.#els.badge.hidden = true;
    this.#els.title.hidden = true;
    this.#els.speaker.hidden = true;
    this.#els.button.hidden = true;
    this.#els.error.hidden = true;
  }

  /**
   * Fade the track text out and back in around a change.
   *
   * The card renders by assigning `textContent`, so there is no element swap to hang a
   * transition on. Adding the class, letting one frame paint, then removing it after the
   * text has been replaced gives the same read as a crossfade with no extra elements.
   * Under `prefers-reduced-motion` the stylesheet neutralises both ends, so this becomes
   * a no-op rather than a flash.
   */
  #markChanging(): void {
    this.#els.card.classList.add("is-changing");
    if (this.#changeTimer !== null) clearTimeout(this.#changeTimer);
    this.#changeTimer = setTimeout(() => {
      this.#els.card.classList.remove("is-changing");
      this.#changeTimer = null;
    }, faMotion.base);
  }

  #render(snap: LinearPlaybackSnapshot): void {
    this.#els.badge.hidden = false;
    this.#els.title.hidden = false;
    this.#els.speaker.hidden = false;
    this.#els.button.hidden = false;

    const nextTitle = snap.display.title;
    if (this.#lastTitle !== null && this.#lastTitle !== nextTitle) {
      this.#markChanging();
    }
    this.#lastTitle = nextTitle;

    this.#els.badge.textContent = snap.display.badge;
    this.#els.badge.className = snap.display.badgeKind === "live" ? "badge badge--live" : "badge";

    this.#els.title.textContent = snap.display.title;
    const speaker = snap.display.speaker.trim();
    this.#els.speaker.textContent = speaker;
    this.#els.speaker.hidden = speaker.length === 0;

    this.#els.button.textContent = buttonLabel(snap);
    this.#els.button.className = buttonClass(snap);
    this.#els.button.disabled =
      (!snap.linearAvailable && !snap.isPlaying && !snap.isTuningIn) ||
      (snap.isTuningIn && !snap.isPlaying);

    if (snap.error) {
      this.#els.error.textContent = snap.error;
      this.#els.error.hidden = false;
    } else {
      this.#els.error.hidden = true;
      this.#els.error.textContent = "";
    }
  }
}

export function registerRiqLinearPlayer(): void {
  if (typeof customElements === "undefined") return;
  if (customElements.get(TAG_NAME)) return;
  customElements.define(TAG_NAME, RiqLinearPlayerElement);
}
